/**
 * Route guards for the SPA.
 *
 *  - ``<UserRoute>``  renders children only when a user is logged in.
 *    Anonymous → redirect to ``/login`` with ``?next=`` carrying the
 *    intended path so we can come back after sign-in.
 *  - ``<AdminRoute>`` renders children only when ``role === "admin"``.
 *    Logged-in non-admin → 403 page (no redirect — they're already
 *    authenticated, the issue is permission).
 *
 * While ``status === "loading"`` (the initial ``/api/auth/me`` call
 * is in flight) we render a thin "Loading…" centred placeholder,
 * NOT redirect — otherwise an authenticated user reloading a deep
 * link would briefly see ``/login`` flash before hydration settles.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { selectStatus, selectUser, useAuth } from "../stores/auth";

function LoadingSplash() {
  return (
    <div className="flex h-full items-center justify-center text-text3">
      <span className="font-mono text-[12px] tracking-wider">LOADING…</span>
    </div>
  );
}

function ForbiddenSplash({ message }: { message: string }) {
  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="flex max-w-md flex-col gap-3 border border-border bg-surface px-6 py-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-red">
          403 — Forbidden
        </div>
        <div className="text-[13px] text-text2">{message}</div>
      </div>
    </div>
  );
}

interface GateProps {
  children: ReactNode;
}

export function UserRoute({ children }: GateProps) {
  const status = useAuth(selectStatus);
  const user = useAuth(selectUser);
  const loc = useLocation();

  if (status === "loading") return <LoadingSplash />;
  if (!user) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

export function AdminRoute({ children }: GateProps) {
  const status = useAuth(selectStatus);
  const user = useAuth(selectUser);
  const loc = useLocation();

  if (status === "loading") return <LoadingSplash />;
  if (!user) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (user.role !== "admin") {
    return (
      <ForbiddenSplash message="This page is admin-only. Ask the operator to grant your account admin role." />
    );
  }
  return <>{children}</>;
}
